<script setup lang="ts">
import { useModyApi } from '~/middelware/ModyApi'
import { useUserDataStorage } from '~/store/userdata'

useSeoMeta({
  title: 'Referenzen – Unsere Events',
  ogTitle: 'Referenzen – Unsere Events | Mody Veranstaltungstechnik',
  description: 'Ausgewählte Events und Projekte von Mody Veranstaltungstechnik – Bühne, Ton & Licht im Salzburger Land.',
  ogDescription: 'Ausgewählte Events und Projekte von Mody Veranstaltungstechnik – Bühne, Ton & Licht im Salzburger Land.',
})

const api = useModyApi()
const userDataStore = useUserDataStorage()
const references = ref<Reference[]>([])
const loaded = ref(false)
const loadedReferenceImages = ref<Set<number>>(new Set())

function markReferenceImageLoaded(id: number) {
  loadedReferenceImages.value = new Set(loadedReferenceImages.value).add(id)
}

async function loadData() {
  references.value = await api.getReferences()
}

onMounted(async () => {
  await loadData()
  loaded.value = true
})
</script>

<template>
  <main>

    <!-- Hero -->
    <div class="public-quiet-hero bg-zinc-900 py-20 px-6 text-center">
      <h1 class="text-4xl sm:text-5xl font-bold text-white mb-5">Referenzen</h1>
      <p class="text-stone-300 text-xl max-w-2xl mx-auto">
        Ausgewählte Events und Projekte – von Firmenveranstaltungen und Hochzeiten bis zu Vereinsfesten und Open-Air-Veranstaltungen im Salzburger Land.
      </p>
    </div>

    <!-- Grid -->
    <div class="public-quiet-section bg-zinc-800 py-16 px-6">
      <div class="max-w-6xl mx-auto">

        <!-- Skeleton -->
        <div v-if="!loaded" class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="i in 3" :key="i" class="bg-zinc-900 rounded-2xl overflow-hidden">
            <Skeleton width="100%" height="220px" />
            <div class="p-5">
              <Skeleton width="60%" class="mb-3" />
              <Skeleton width="85%" />
            </div>
          </div>
        </div>

        <!-- Cards -->
        <div v-else class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <NuxtLink
            v-for="ref in references"
            :key="ref.id"
            :to="'/references/' + ref.id"
            class="reference-card bg-zinc-900 rounded-xl overflow-hidden group transition-colors no-underline"
          >
            <div class="reference-image-shell h-[220px] overflow-hidden bg-zinc-800" :aria-busy="ref.image.url && !loadedReferenceImages.has(ref.id)">
              <Skeleton
                v-if="ref.image.url && !loadedReferenceImages.has(ref.id)"
                class="reference-image-skeleton"
                aria-hidden="true"
              />
              <NuxtImg
                v-if="ref.image.url"
                :src="ref.image.url"
                :alt="ref.title"
                class="reference-image w-full h-full object-cover group-hover:scale-105 transition-[transform,opacity] duration-300"
                :class="{ 'reference-image--loaded': loadedReferenceImages.has(ref.id) }"
                format="webp"
                quality="80"
                loading="lazy"
                @load="markReferenceImageLoaded(ref.id)"
                @error="markReferenceImageLoaded(ref.id)"
              />
              <div v-else class="w-full h-full flex items-center justify-center">
                <i class="pi pi-image text-4xl text-zinc-600"></i>
              </div>
            </div>
            <div class="p-5">
              <h2 class="text-lg font-bold text-white mb-1 group-hover:text-[#E39737] transition-colors">{{ ref.title }}</h2>
              <p v-if="ref.description" class="text-stone-400 text-sm leading-relaxed">{{ ref.description }}</p>
            </div>
          </NuxtLink>
        </div>

        <!-- Keine Einträge -->
        <div v-if="loaded && references.length === 0" class="text-center py-16 text-stone-400">
          Noch keine Referenzen vorhanden.
        </div>

        <!-- Admin: Hinzufügen -->
        <div v-if="userDataStore.loggedIn" class="mt-10 text-center">
          <Button severity="secondary" @click="router.push('/references/-1')">
            <i class="pi pi-plus mr-2"></i> Referenz hinzufügen
          </Button>
        </div>

      </div>
    </div>

    <!-- CTA -->
    <div class="public-quiet-section bg-zinc-900 py-16 px-6 text-center">
      <h2 class="text-3xl font-bold text-white mb-4">Euer Event als nächstes?</h2>
      <p class="text-stone-300 text-lg mb-8 max-w-xl mx-auto">
        Kostenlos & unverbindlich anfragen – wir melden uns schnell mit einem passenden Angebot für Firmenveranstaltungen, Hochzeiten und regionale Events.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <Button as="router-link" to="/shop/request" class="px-8">Angebot anfordern</Button>
        <Button as="router-link" to="/contact" severity="secondary" class="px-8">Kontakt aufnehmen</Button>
      </div>
    </div>

  </main>
</template>

<style scoped lang="scss">
.reference-card{
  border: 1px solid $public-border;

  &:hover,
  &:focus-visible{
    border-color: rgba(213, 150, 69, 0.42);
  }
}

.reference-image-shell{
  position: relative;
}

.reference-image-skeleton{
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  border-radius: 0 !important;
}

.reference-image{
  opacity: 0;
}

.reference-image--loaded{
  opacity: 1;
}
</style>
